import { useState } from 'react'
import { Box, Button, CircularProgress, InputAdornment, TextField, Typography } from '@mui/material'

import { useAuth } from '../../../context/SecurityContextProvider'
import { checkUsernameAvailability, setUsername as setUsernameApi } from '../../../services/api/UserDataService'
import { onboardingStyles as s } from '../onboardingStyles'

export default function UsernameStep({ userData, goNext }) {
    const { setUserData } = useAuth()
    const [username, setUsername] = useState('')
    const [isValid, setIsValid] = useState(false)
    const [message, setMessage] = useState('')
    const [checking, setChecking] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [debounce, setDebounce] = useState(null)

    const runCheck = async (value) => {
        if (!value) {
            setIsValid(false)
            setMessage('')
            setChecking(false)
            return
        }
        const [available, msg] = await checkUsernameAvailability(value)
        setIsValid(Boolean(available))
        setMessage(msg || '')
        setChecking(false)
    }

    const handleChange = (e) => {
        const value = e.target.value.trim()
        setUsername(value)
        setIsValid(false)
        if (debounce) clearTimeout(debounce)
        if (value) {
            setChecking(true)
            setDebounce(setTimeout(() => runCheck(value), 450))
        } else {
            setMessage('')
            setChecking(false)
        }
    }

    const handleSubmit = async () => {
        setSubmitting(true)
        try {
            // New API: identity comes from the access token (no userId), and the call returns the
            // updated user (didCompleteSetup === true). Update the cached user instead of mutating it.
            const updated = await setUsernameApi(username)
            setUserData(updated)
            goNext()
        } catch (error) {
            setSubmitting(false)
            setMessage('Something went wrong. Please try again.')
            setIsValid(false)
        }
    }

    const firstName = userData?.username?.split(' ')[0] || 'there'

    return (
        <Box>
            <Typography sx={s.title}>Hi there 👋</Typography>
            <Typography sx={s.subtitle}>
                Choose your username. This will be your Vibio handle.
            </Typography>

            <TextField
                fullWidth
                autoFocus
                label="username"
                variant="outlined"
                value={username}
                onChange={handleChange}
                InputProps={{
                    startAdornment: <InputAdornment position="start">vibio.co/</InputAdornment>,
                    endAdornment: checking ? <CircularProgress size={18} /> : null,
                }}
            />

            <Box sx={{ minHeight: 24, mt: 1 }}>
                {username && !checking && (
                    <Typography
                        variant="body2"
                        sx={{ color: isValid ? '#2e7d32' : '#c62828', fontWeight: 600 }}
                    >
                        {isValid ? 'Looks good!' : message}
                    </Typography>
                )}
            </Box>

            <Box sx={s.footer}>
                <Button
                    variant="contained"
                    disableElevation
                    disabled={!isValid || submitting}
                    onClick={handleSubmit}
                    sx={s.ctaButton}
                >
                    {submitting ? <CircularProgress size={22} color="inherit" /> : 'Continue'}
                </Button>
            </Box>
        </Box>
    )
}
